/**
 * Rewards Filter Component
 * Handles tag filtering and pagination for rewards content sections
 */
class RewardsFilter extends HTMLElement {
  /** @type {HTMLElement[]} */
  cards = [];
  /** @type {Set<string>} */
  activeTags = new Set();
  /** @type {number} */
  currentPage = 1;
  /** @type {number} */
  itemsPerPage = 8;
  /** @type {HTMLElement[]} */
  filteredCards = [];
  /** @type {number} */
  totalPages = 1;
  /** @type {HTMLElement|null} */
  filterBar = null;
  /** @type {HTMLElement|null} */
  grid = null;
  /** @type {HTMLElement|null} */
  noResults = null;
  /** @type {HTMLElement|null} */
  statusText = null;
  /** @type {HTMLElement|null} */
  pagination = null;
  /** @type {HTMLElement|null} */
  paginationNumbers = null;
  /** @type {HTMLButtonElement|null} */
  prevBtn = null;
  /** @type {HTMLButtonElement|null} */
  nextBtn = null;
  /** @type {HTMLButtonElement|null} */
  drawerTrigger = null;
  /** @type {HTMLElement|null} */
  drawerPanel = null;
  /** @type {HTMLElement|null} */
  drawerScrim = null;
  /** @type {HTMLButtonElement|null} */
  drawerClose = null;
  /** @type {string} */
  sectionId = '';

  constructor() {
    super();
  }

  connectedCallback() {
    this.init();
  }

  init() {
    // Get data attributes
    this.itemsPerPage = parseInt(this.dataset.itemsPerPage) || 6;
    this.sectionId = this.dataset.sectionId || '';

    // Get DOM elements
    this.filterBar = this.querySelector('.rewards-filter-bar');
    this.grid = this.querySelector('.rewards-filter-grid');
    this.cards = Array.from(this.querySelectorAll('.rewards-filter-card'));
    this.noResults = this.querySelector('.rewards-filter-no-results');
    this.statusText = this.querySelector('.rewards-filter-status-text');
    this.pagination = this.querySelector('.rewards-filter-pagination');
    this.paginationNumbers = this.querySelector('.rewards-filter-pagination-numbers');
    this.prevBtn = this.querySelector('.rewards-filter-pagination-btn--prev');
    this.nextBtn = this.querySelector('.rewards-filter-pagination-btn--next');

    // Initialize
    this.bindEvents();
    this.filterCards();
    this.updateDrawerTrigger();
  }

  bindEvents() {
    // Delegated tag filter button clicks (works for both main bar and drawer)
    this.addEventListener('click', (e) => {
      const tagButton = e.target.closest('.rewards-filter-tag');
      if (tagButton) {
        console.log('Tag button clicked via delegation:', tagButton.dataset.tag);
        this.handleTagClick(tagButton);
      }
    });

    // Also attach event listener specifically to drawer panel if it exists
    if (this.drawerPanel) {
      this.drawerPanel.addEventListener('click', (e) => {
        const tagButton = e.target.closest('.rewards-filter-tag');
        if (tagButton) {
          console.log('Drawer tag button clicked:', tagButton.dataset.tag);
          // Prevent the event from bubbling to avoid double handling
          e.stopPropagation();
          this.handleTagClick(tagButton);
        }
      });
    }

    // Tag more/less button clicks (for expanding/collapsing tags in cards)
    this.addEventListener('click', (e) => {
      const tagMoreButton = e.target.closest('[data-tag-more]');
      const tagLessButton = e.target.closest('[data-tag-less]');
      if (tagMoreButton) {
        this.handleTagToggle(e, tagMoreButton, true);
      } else if (tagLessButton) {
        this.handleTagToggle(e, tagLessButton, false);
      }
    });

    // Pagination buttons
    this.prevBtn?.addEventListener('click', () => this.goToPage(this.currentPage - 1));
    this.nextBtn?.addEventListener('click', () => this.goToPage(this.currentPage + 1));
    
    // Drawer trigger
    this.drawerTrigger = this.querySelector('[data-drawer-trigger]');
    this.drawerPanel = this.querySelector('[data-drawer-panel]');
    this.drawerScrim = this.querySelector('[data-drawer-scrim]');
    this.drawerClose = this.querySelector('[data-drawer-close]');
    
    if (this.drawerTrigger) {
      this.drawerTrigger.addEventListener('click', () => this.openDrawer());
    }
    
    if (this.drawerScrim) {
      this.drawerScrim.addEventListener('click', () => this.closeDrawer());
    }
    
    if (this.drawerClose) {
      this.drawerClose.addEventListener('click', () => this.closeDrawer());
    }
    
    // Close drawer on Escape key
    this.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.drawerPanel && !this.drawerPanel.hidden) {
        this.closeDrawer();
      }
    });

    // Initialize tag hover support for desktop
    this.initTagHoverSupport();
  }

  handleTagClick(tagButton) {
    const tagValue = tagButton.dataset.tag;

    console.log('Tag clicked:', tagValue, 'Current active tags:', Array.from(this.activeTags));

    if (tagValue === 'all') {
      // Clear all filters
      this.activeTags.clear();
      this.querySelectorAll('.rewards-filter-tag').forEach(t => {
        t.classList.remove('active');
        t.setAttribute('aria-pressed', 'false');
      });
      tagButton.classList.add('active');
      tagButton.setAttribute('aria-pressed', 'true');

      // Close drawer if open
      this.closeDrawer();
    } else {
      // Toggle individual tag
      const allButton = this.querySelector('.rewards-filter-tag--all');

      if (this.activeTags.has(tagValue)) {
        this.activeTags.delete(tagValue);
        tagButton.classList.remove('active');
        tagButton.setAttribute('aria-pressed', 'false');
      } else {
        this.activeTags.add(tagValue);
        tagButton.classList.add('active');
        tagButton.setAttribute('aria-pressed', 'true');
      }

      // Update "All" button state
      if (this.activeTags.size === 0) {
        allButton.classList.add('active');
        allButton.setAttribute('aria-pressed', 'true');
      } else {
        allButton.classList.remove('active');
        allButton.setAttribute('aria-pressed', 'false');
      }
    }

    console.log('After click - Active tags:', Array.from(this.activeTags));

    // Reset to page 1 and re-filter
    this.currentPage = 1;
    this.filterCards();
    this.updateDrawerTrigger();
  }

  /**
   * Handle tag more/less button click to expand/collapse hidden tags
   */
  handleTagToggle(event, button, isExpanding) {
    event.stopPropagation();
    event.preventDefault();

    const wrapper = button.closest('[data-tag-wrapper]');
    if (!wrapper) return;

    if (isExpanding) {
      wrapper.classList.add('expanded');
      button.setAttribute('aria-expanded', 'true');
    } else {
      wrapper.classList.remove('expanded');
      button.setAttribute('aria-expanded', 'false');
    }
  }

  /**
   * Initialize tag hover support for desktop
   */
  initTagHoverSupport() {
    const tagWrappers = this.querySelectorAll('[data-tag-wrapper]');
    
    tagWrappers.forEach(wrapper => {
      const card = wrapper.closest('.rewards-card');
      if (!card) return;

      // Desktop: Expand tags on card hover
      card.addEventListener('mouseenter', () => {
        if (window.matchMedia('(min-width: 769px)').matches) {
          wrapper.classList.add('expanded');
        }
      });

      card.addEventListener('mouseleave', () => {
        if (window.matchMedia('(min-width: 769px)').matches) {
          wrapper.classList.remove('expanded');
        }
      });
    });
  }

  /**
   * Filter cards based on active tags
   */
  filterCards() {
    this.filteredCards = this.cards.filter(card => {
      if (this.activeTags.size === 0) return true;
      
      if (!(card instanceof HTMLElement)) return false;
      const cardTags = card.dataset.tags || '';
      const cardTagArray = cardTags.split(',').map(/** @param {string} t */ t => t.trim().toLowerCase());
      
      // AND logic: card is visible if it has ALL of the active tags
      return Array.from(this.activeTags).every(activeTag => cardTagArray.includes(activeTag));
    });

    this.totalPages = Math.ceil(this.filteredCards.length / this.itemsPerPage);
    
    console.log('Filter results:');
    console.log('- Filtered cards:', this.filteredCards.length);
    console.log('- Total pages calculated:', this.totalPages);
    console.log('- Items per page:', this.itemsPerPage);
    
    // Clamp current page if it's now beyond the total
    if (this.currentPage > this.totalPages && this.totalPages > 0) {
      this.currentPage = this.totalPages;
    } else if (this.currentPage < 1) {
      this.currentPage = 1;
    }

    this.updateCardVisibility();
    this.updatePagination();
    this.updateStatus();
  }

  updateCardVisibility() {
    // Hide all cards first
    this.cards.forEach(card => {
      if (card instanceof HTMLElement) {
        card.hidden = true;
      }
    });

    // Show no results message if needed
    if (this.filteredCards.length === 0) {
      if (this.noResults) this.noResults.hidden = false;
      if (this.grid) this.grid.hidden = true;
      return;
    } else {
      if (this.noResults) this.noResults.hidden = true;
      if (this.grid) this.grid.hidden = false;
    }

    // Calculate which cards to show for current page
    const startIndex = (this.currentPage - 1) * this.itemsPerPage;
    const endIndex = startIndex + this.itemsPerPage;
    const cardsToShow = this.filteredCards.slice(startIndex, endIndex);

    // Show only the cards for current page
    cardsToShow.forEach(card => {
      if (card instanceof HTMLElement) {
        card.hidden = false;
      }
    });
  }

  /**
   * Update pagination controls visibility and state
   */
  updatePagination() {
    // Only show pagination if there are multiple pages
    if (this.totalPages <= 1) {
      if (this.pagination) this.pagination.hidden = true;
      return;
    }

    if (this.pagination) this.pagination.hidden = false;

    // Update prev/next buttons state
    if (this.prevBtn) this.prevBtn.disabled = this.currentPage === 1;
    if (this.nextBtn) this.nextBtn.disabled = this.currentPage === this.totalPages;

    // Update page numbers
    this.paginationNumbers.innerHTML = '';
    
    // Determine which page numbers to show
    let startPage = Math.max(1, this.currentPage - 2);
    let endPage = Math.min(this.totalPages, this.currentPage + 2);
    
    // Always show at least 5 page numbers if possible
    if (endPage - startPage < 4) {
      if (startPage === 1) {
        endPage = Math.min(this.totalPages, startPage + 4);
      } else if (endPage === this.totalPages) {
        startPage = Math.max(1, endPage - 4);
      }
    }

    // Add first page and ellipsis if needed
    if (startPage > 1) {
      this.createPageButton(1);
      if (startPage > 2) {
        this.createEllipsis();
      }
    }

    // Add page numbers
    for (let i = startPage; i <= endPage; i++) {
      this.createPageButton(i);
    }

    // Add last page and ellipsis if needed
    if (endPage < this.totalPages) {
      if (endPage < this.totalPages - 1) {
        this.createEllipsis();
      }
      this.createPageButton(this.totalPages);
    }
  }

  createPageButton(pageNum) {
    const button = document.createElement('button');
    button.className = 'rewards-filter-pagination-number';
    button.textContent = pageNum;
    button.setAttribute('aria-label', `Go to page ${pageNum}`);
    
    if (pageNum === this.currentPage) {
      button.classList.add('active');
      button.setAttribute('aria-current', 'page');
    }
    
    button.addEventListener('click', () => this.goToPage(pageNum));
    if (this.paginationNumbers) {
      this.paginationNumbers.appendChild(button);
    }
  }

  createEllipsis() {
    const span = document.createElement('span');
    span.className = 'rewards-filter-pagination-ellipsis';
    span.textContent = '...';
    span.setAttribute('aria-hidden', 'true');
    if (this.paginationNumbers) {
      this.paginationNumbers.appendChild(span);
    }
  }

  goToPage(pageNum) {
    if (pageNum < 1 || pageNum > this.totalPages || pageNum === this.currentPage) return;
    
    this.currentPage = pageNum;
    this.updateCardVisibility();
    this.updatePagination();
    this.updateStatus();
    
    // Scroll to top of grid
    if (this.grid) this.grid.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  updateStatus() {
    if (!this.statusText) return;

    const total = this.filteredCards.length;
    if (total === 0) {
      this.statusText.textContent = 'No results';
      return;
    }

    const start = (this.currentPage - 1) * this.itemsPerPage + 1;
    const end = Math.min(this.currentPage * this.itemsPerPage, total);
    
    this.statusText.textContent = `Showing ${start}–${end} of ${total} results`;
  }
  
  openDrawer() {
    if (!this.drawerPanel || !this.drawerScrim || !this.drawerTrigger) return;

    console.log('Opening drawer');
    this.drawerPanel.hidden = false;
    this.drawerScrim.hidden = false;
    this.drawerTrigger.setAttribute('aria-expanded', 'true');

    // Lock body scroll
    document.body.style.overflow = 'hidden';

    // Move focus to first focusable button in drawer
    const firstButton = this.drawerPanel.querySelector('.rewards-filter-tag');
    if (firstButton instanceof HTMLElement) {
      setTimeout(() => firstButton.focus(), 100);
    }

    // Log all drawer tags for debugging
    const drawerTags = this.drawerPanel.querySelectorAll('.rewards-filter-tag');
    console.log('Drawer tags found:', drawerTags.length);
    drawerTags.forEach((tag, index) => {
      console.log(`Drawer tag ${index}:`, tag.dataset.tag, tag.textContent.trim());
    });
  }
  
  closeDrawer() {
    if (!this.drawerPanel || !this.drawerScrim || !this.drawerTrigger) return;
    
    this.drawerPanel.hidden = true;
    this.drawerScrim.hidden = true;
    this.drawerTrigger.setAttribute('aria-expanded', 'false');
    
    // Restore body scroll
    document.body.style.overflow = '';
    
    // Return focus to trigger button
    if (this.drawerTrigger instanceof HTMLElement) {
      this.drawerTrigger.focus();
    }
  }
  
  updateDrawerTrigger() {
    if (!this.drawerTrigger) return;
    
    // Count active secondary tags (tags inside the drawer panel)
    const secondaryTags = this.drawerPanel?.querySelectorAll('.rewards-filter-tag[data-tag]') || [];
    let activeSecondaryCount = 0;
    
    secondaryTags.forEach(tag => {
      if (tag instanceof HTMLElement && tag.dataset.tag) {
        const tagValue = tag.dataset.tag;
        if (this.activeTags.has(tagValue)) {
          activeSecondaryCount++;
        }
      }
    });
    
    const countSpan = this.drawerTrigger.querySelector('.rewards-filter-more-count');
    if (countSpan) {
      if (activeSecondaryCount > 0) {
        countSpan.textContent = `(${activeSecondaryCount} active)`;
      } else {
        countSpan.textContent = `(${secondaryTags.length})`;
      }
    }
  }
}

// Register the custom element
if (!customElements.get('rewards-filter')) {
  customElements.define('rewards-filter', RewardsFilter);
}
